if (!customElements.get('marame-back-in-stock')) {
  customElements.define('marame-back-in-stock', class MarameBackInStock extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dialog = this.querySelector('dialog');
      this.form = this.querySelector('[data-notify-form]');
      this.email = this.querySelector('[data-notify-email]');
      this.smsConsentPanel = this.querySelector('[data-notify-sms-consent-panel]');
      this.phone = this.querySelector('[data-notify-phone]');
      this.smsConsent = this.querySelector('[data-notify-sms-consent]');
      this.smsEnabled = this.dataset.smsEnabled === 'true' && /^[a-zA-Z0-9]{6}$/.test(this.dataset.smsList || '') && !!this.phone;
      this.email.addEventListener('input', () => this.updateContactFields(), options);
      this.phone?.addEventListener('input', () => this.updateContactFields(), options);
      this.submitButton = this.querySelector('[data-notify-submit]');
      this.status = this.querySelector('[data-notify-status]');
      this.error = this.querySelector('[data-notify-error]');
      this.item = this.querySelector('[data-notify-item]');
      this.requests ||= new Map();
      this.accepted ||= new Set();
      this.uncertain ||= new Set();
      this.viewToken ||= 0;
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-marame-notify]');
        if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
        const details = { variantId: trigger.dataset.variantId, productTitle: trigger.dataset.productTitle, variantTitle: trigger.dataset.variantTitle, trigger };
        if (this.show(details)) event.preventDefault();
      }, options);
      document.addEventListener('marame:notify', (event) => this.show(event.detail || {}), options);
      this.form.addEventListener('submit', (event) => this.submit(event), options);
      this.querySelector('[data-notify-close]').addEventListener('click', () => this.hide(), options);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.hide(); }, options);
      this.dialog.addEventListener('pointerdown', (event) => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('click', (event) => {
        if (this.backdropDown && this.isBackdrop(event)) this.hide();
        this.backdropDown = false;
      }, options);
      this.dialog.addEventListener('close', () => {
        if (this.hasAttribute('open')) this.hide();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort();
      this.viewToken++;
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
    }

    validVariant(id) {
      return /^[1-9]\d{0,19}$/.test(String(id || ''));
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }

    show(details) {
      if (!this.validVariant(details.variantId) || !this.dialog?.showModal) return false;
      this.viewToken++;
      this.variantId = String(details.variantId);
      this.openedBy = details.trigger || document.activeElement;
      this.parentBag = this.openedBy?.closest?.('cart-drawer');
      if (this.parentBag?.classList.contains('active') && typeof removeTrapFocus === 'function') removeTrapFocus();
      this.item.textContent = [details.productTitle, details.variantTitle].filter(Boolean).join(' · ');
      this.form.reset();
      this.updateContactFields();
      this.form.hidden = false;
      this.error.hidden = true;
      this.status.hidden = true;
      this.setBusy(false);
      if (!this.dialog.open) {
        this.ownsBodyLock = !document.body.classList.contains('overflow-hidden');
        document.body.classList.add('overflow-hidden');
        this.dialog.showModal();
      }
      this.setAttribute('open', '');
      this.email.focus({ preventScroll: true });
      return true;
    }

    hide() {
      this.viewToken++;
      this.dialog.close();
      this.removeAttribute('open');
      this.form.reset();
      if (this.ownsBodyLock) document.body.classList.remove('overflow-hidden');
      this.ownsBodyLock = false;
      if (this.parentBag?.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(this.parentBag.querySelector('.drawer__inner'), this.openedBy?.isConnected ? this.openedBy : this.parentBag.querySelector('.drawer__close'));
      } else if (this.openedBy?.isConnected) {
        this.openedBy.focus({ preventScroll: true });
      }
    }

    setBusy(busy) {
      this.busy = busy;
      this.form.setAttribute('aria-busy', String(busy));
      this.submitButton.disabled = busy;
      this.email.disabled = busy;
      const wantsSms = this.smsEnabled && !!this.phone?.value.trim();
      if (this.phone) this.phone.disabled = busy || !this.smsEnabled;
      if (this.smsConsent) this.smsConsent.disabled = busy || !wantsSms;
      this.submitButton.textContent = busy ? this.dataset.pendingLabel : this.dataset.submitLabel;
    }

    updateContactFields() {
      const wantsSms = this.smsEnabled && !!this.phone?.value.trim();
      this.email.required = !wantsSms;
      this.email.setCustomValidity('');
      if (this.smsConsentPanel) this.smsConsentPanel.hidden = !wantsSms;
      if (this.phone) {
        this.phone.disabled = !!this.busy || !this.smsEnabled;
        this.phone.required = false;
        this.phone.setCustomValidity('');
      }
      if (this.smsConsent) {
        this.smsConsent.disabled = !!this.busy || !wantsSms;
        this.smsConsent.required = !!wantsSms;
        if (!wantsSms) this.smsConsent.checked = false;
      }
    }

    normalizeUKMobile(value) {
      let number = String(value || '').trim().replace(/[\s().-]/g, '');
      if (number.startsWith('0044')) number = '+' + number.slice(2);
      if (number.startsWith('07')) number = '+44' + number.slice(1);
      // UK mobile prefixes only; 070 personal numbers and 076 pagers are excluded.
      return /^\+447[1-57-9]\d{8}$/.test(number) ? number : null;
    }

    postOnce(key, path, payload) {
      if (this.accepted.has(key)) return Promise.resolve({ code: 'accepted' });
      if (this.uncertain.has(key)) return Promise.resolve({ code: 'uncertain' });
      if (this.requests.has(key)) return this.requests.get(key);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 20000);
      const request = (async () => {
        try {
          const response = await fetch(`https://a.klaviyo.com/client/${path}/?company_id=${encodeURIComponent(this.dataset.publicKey)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', revision: '2026-10-15' },
            body: JSON.stringify(payload), credentials: 'omit', redirect: 'error', signal: controller.signal
          });
          if (response.status === 202) { this.accepted.add(key); return { code: 'accepted' }; }
          if (response.status === 404) return { code: 'unavailable' };
          if (response.status === 429) return { code: 'rate' };
          return { code: 'error' };
        } catch {
          // A connection failure may occur after acceptance. Never auto-resubmit it.
          this.uncertain.add(key);
          return { code: 'uncertain' };
        } finally {
          clearTimeout(timeout);
          this.requests.delete(key);
        }
      })();
      this.requests.set(key, request);
      return request;
    }

    async requestSubscription(variantId, email = '', phone = null) {
      email = String(email || '').trim();
      if (!this.validVariant(variantId) || !/^[a-zA-Z0-9]{6}$/.test(this.dataset.publicKey || '') || (!email && !phone)) return { code: 'unavailable' };
      if (phone && (!this.smsEnabled || this.normalizeUKMobile(phone) !== phone)) return { code: 'unavailable' };
      const channels = [email ? 'EMAIL' : null, phone ? 'SMS' : null].filter(Boolean);
      const attributes = {};
      if (email) attributes.email = email;
      if (phone) attributes.phone_number = phone;
      const key = 'stock:' + channels.join('+') + ':' + variantId + ':' + email.toLowerCase() + ':' + (phone || '');
      const result = await this.postOnce(key, 'back-in-stock-subscriptions', { data: {
        type: 'back-in-stock-subscription',
        attributes: { profile: { data: { type: 'profile', attributes } }, channels },
        relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } } }
      } });
      return result;
    }

    async requestSmsConsent(email, phone, consent) {
      if (!this.smsEnabled || consent !== true || this.normalizeUKMobile(phone) !== phone) return { code: 'unavailable' };
      // Klaviyo classifies restock texts as SMS marketing. Scope remains explicit:
      // the dedicated list and purpose property must be excluded from general SMS campaigns.
      email = String(email || '').trim();
      const subscription = await this.postOnce(`consent:${email.toLowerCase()}:${phone}`, 'subscriptions', { data: {
        type: 'subscription',
        attributes: {
          custom_source: 'MARAME requested restock texts only · v2 2026-10-27',
          profile: { data: { type: 'profile', attributes: {
            ...(email ? { email } : {}), phone_number: phone,
            properties: { 'MARAME requested restock texts': true, 'MARAME restock SMS disclosure': 'v2 2026-10-27' },
            subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: this.dataset.smsList } } }
      } });
      return subscription;
    }

    async submit(event) {
      event.preventDefault();
      if (this.busy) return;
      this.email.value = this.email.value.trim();
      this.updateContactFields();
      const email = this.email.value;
      const wantsSms = this.smsEnabled && !!this.phone?.value.trim();
      const phone = wantsSms ? this.normalizeUKMobile(this.phone.value) : null;
      if (!email && !wantsSms) this.email.setCustomValidity(this.dataset.contactRequiredLabel);
      if (wantsSms && !phone) this.phone.setCustomValidity(this.dataset.phoneErrorLabel);
      if (!this.form.reportValidity()) return;
      const token = this.viewToken;
      const variantId = this.variantId;
      this.error.hidden = true;
      this.status.hidden = true;
      this.setBusy(true);
      const smsConsent = wantsSms && this.smsConsent.checked;
      const consentResult = wantsSms ? await this.requestSmsConsent(email, phone, smsConsent) : null;
      const includeSms = wantsSms && consentResult?.code === 'accepted';
      const result = wantsSms && !includeSms
        ? consentResult || { code: 'unavailable' }
        : await this.requestSubscription(variantId, email, includeSms ? phone : null);
      if (token !== this.viewToken || !this.dialog.open || !this.isConnected) return;
      this.setBusy(false);
      if (result.code === 'accepted') {
        this.form.hidden = true;
        this.form.reset();
        this.updateContactFields();
        this.status.textContent = !wantsSms ? this.dataset.successLabel
          : email ? this.dataset.smsSuccessLabel : this.dataset.smsOnlySuccessLabel;
        this.status.hidden = false;
        this.status.focus({ preventScroll: true });
      } else {
        this.error.textContent = this.dataset[result.code + 'Label'] || this.dataset.errorLabel;
        this.error.hidden = false;
        if (result.code === 'uncertain') this.form.hidden = true;
      }
    }
  });
}
